<template>
  <div>
    <simpletoolbar />

    <!-- Hero -->
    <section class="bg-gradient-to-b from-primary-50 to-white dark:from-stone-900 dark:to-stone-800 py-14 px-4">
      <div class="max-w-3xl mx-auto text-center">
        <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary-100 dark:bg-primary-900 mb-6">
          <img src="/images/favicon-32x32.png" alt="Spin the Names Logo" class="w-8 h-8" />
        </div>
        <h1 class="text-4xl font-bold text-stone-800 dark:text-stone-100 mb-4">About Spin the Names</h1>
        <p class="text-xl text-stone-500 dark:text-stone-400 max-w-2xl mx-auto">
          A free, transparent random name picker built for teachers, organizers, streamers, and anyone who needs a fair spin.
        </p>
      </div>
    </section>

    <div class="max-w-3xl mx-auto px-4 py-12 space-y-14">

      <!-- The story -->
      <section>
        <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-4">The Story</h2>
        <div class="text-stone-600 dark:text-stone-300 leading-relaxed space-y-4">
          <p>
            There are already name picker tools out there, including ones backed by large tech companies. But tools built by big corporations tend to reflect big-corporation priorities: data collection, ad revenue, growth metrics. They're not built for the teacher trying to keep a classroom fair, the streamer trying to reward their community, or the team lead trying to make Monday morning standup a little less awkward.
          </p>
          <p>
            Spin the Names is built by an independent developer for regular people. No corporate agenda, no dark patterns, no selling your data to make quarterly numbers. Just a tool that does what it says, works well, and is free to use.
          </p>
          <p>
            When you use Spin the Names, you're supporting independent software development over big tech. That matters — and it's appreciated.
          </p>
        </div>
      </section>

      <!-- What makes it different -->
      <section>
        <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-6">What Makes It Different</h2>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i class="fas fa-lock-open text-primary-600 dark:text-primary-400"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">Free to Use</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm">No paywall, no trial, no credit card. The core tool is free for everyone.</p>
            </div>
          </div>

          <div class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i class="fas fa-eye text-primary-600 dark:text-primary-400"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">Transparent</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm">Everyone can see the spin happen. No behind-the-scenes draws, no questions about fairness.</p>
            </div>
          </div>

          <div class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i class="fas fa-globe text-primary-600 dark:text-primary-400"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">Multilingual</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm">Available in English, Spanish, French, German, and Swedish.</p>
            </div>
          </div>

          <div class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i class="fas fa-mobile-alt text-primary-600 dark:text-primary-400"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">Works Everywhere</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm">Any browser, any device. No app to download, no plugins required.</p>
            </div>
          </div>

          <div class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i class="fas fa-save text-primary-600 dark:text-primary-400"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">Save Your Wheels</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm">Create a free account to save and reuse wheels across sessions. Your class list, always ready.</p>
            </div>
          </div>

          <div class="flex gap-4 p-5 rounded-xl border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-800">
            <div class="flex-shrink-0 w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900 flex items-center justify-center">
              <i class="fas fa-shield-alt text-primary-600 dark:text-primary-400"></i>
            </div>
            <div>
              <h3 class="font-semibold text-stone-800 dark:text-stone-100 mb-1">Privacy Respecting</h3>
              <p class="text-stone-500 dark:text-stone-400 text-sm">No selling your data. No tracking beyond what's needed to keep the service running.</p>
            </div>
          </div>
        </div>
      </section>

      <!-- Who built it -->
      <section>
        <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-4">Who Built This</h2>
        <div class="text-stone-600 dark:text-stone-300 leading-relaxed space-y-4">
          <p>
            Hi, I'm Ash — a software engineer and musician based in Austin, TX. I build tools I wish existed, and Spin the Names is one of them.
          </p>
          <p>
            I believe software should be useful, honest, and accessible. Spin the Names is my attempt at all three: a tool that does one thing well, doesn't manipulate you, and works for anyone who needs it regardless of budget.
          </p>
          <p>
            Building and maintaining this is something I do independently. If you find it useful, the best thing you can do is share it with someone else who would too.
          </p>
        </div>
      </section>

      <!-- Contact -->
      <section>
        <h2 class="text-2xl font-bold text-stone-800 dark:text-stone-100 mb-4">Get in Touch</h2>
        <p class="text-stone-600 dark:text-stone-300 leading-relaxed mb-6">
          Have a question, a bug to report, or a feature idea? I read everything, though I can't always reply to every message.
        </p>
        <a
          href="mailto:ash@spinthenames.com"
          class="inline-flex items-center gap-3 bg-primary-600 hover:bg-primary-700 dark:hover:bg-primary-800 text-white dark:text-white font-semibold px-6 py-3 rounded-lg transition-colors"
        >
          <i class="fas fa-envelope"></i>
          ash@spinthenames.com
        </a>
      </section>

      <!-- CTA -->
      <section class="text-center border-t border-stone-200 dark:border-stone-700 pt-10">
        <p class="text-stone-500 dark:text-stone-400 mb-4">Ready to spin?</p>
        <router-link
          to="/"
          class="inline-block bg-primary-600 hover:bg-primary-700 dark:hover:bg-primary-800 text-white dark:text-white font-semibold px-8 py-3 rounded-lg transition-colors"
        >
          Try Spin the Names Free →
        </router-link>
      </section>

    </div>

    <app-footer />
  </div>
</template>

<script setup>
import simpletoolbar from '@components/ui/simpletoolbar.vue';
import appFooter from '@components/ui/appFooter.vue';
import { useSeo } from '@/composables/useSeo';

useSeo({
  title: 'About | Spin the Names',
  description:
    'Spin the Names is a free, transparent random name picker built for teachers, streamers, event organizers, and teams. Built independently in Austin, TX.',
  structuredData: {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Spin the Names',
    description:
      'Spin the Names is a free, transparent random name picker built for teachers, streamers, event organizers, and teams.',
    url: 'https://spinthenames.com/about',
    mainEntity: {
      '@type': 'Person',
      name: 'Ash',
      jobTitle: 'Software Engineer',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Austin',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      email: 'ash@spinthenames.com',
    },
  },
});
</script>
